/**
 * 文件上传组合式函数
 */
import { ref } from 'vue'
import { Message } from '@arco-design/web-vue'
import { request } from '@/utils/http'
import { getTokenManager } from '@/utils/token'

// 图片缓存管理器
class ImageCacheManager {
  private cache = new Map<string, string>() // fileId -> blob URL
  private maxCacheSize = 50 // 最大缓存数量
  /** 同一 fileId 正在进行的下载，避免并发重复拉取（watch + 显式调用会同时触发） */
  private pending = new Map<string, Promise<string>>()

  // 缓存图片（失败自动重试：无痕/首次登录时 token / 上下文可能尚未就绪）
  async cacheImage(fileId: string): Promise<string> {
    const cached = this.cache.get(fileId)
    if (cached) {
      return cached
    }

    const inFlight = this.pending.get(fileId)
    if (inFlight) {
      return inFlight
    }

    const task = this.downloadAndCache(fileId).finally(() => {
      this.pending.delete(fileId)
    })
    this.pending.set(fileId, task)
    return task
  }

  /**
   * 实际下载并写入缓存。
   *
   * 后端 download 失败时会吞掉异常返回 200 空 body（甚至 XML/HTML 报错页），
   * 所以这里必须校验内容；否则空 / 报错结果会被当成成功写进内存缓存，
   * 导致整个 SPA 会话再也刷不出图，只有整页刷新（内存缓存被清空）才恢复。
   */
  private async downloadAndCache(fileId: string): Promise<string> {
    const maxAttempts = 3
    let lastError: unknown = null

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // 使用/findByFileId/{fileId}接口下载图片
        const token = getTokenManager().get()
        const headers: Record<string, string> = {}

        if (token) {
          // 直接使用token值，不添加Bearer前缀
          const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token
          headers.Authorization = cleanToken
        }

        const response = await fetch(this.getDownloadUrl(fileId), {
          headers
        })

        if (!response.ok) {
          throw new Error(`下载失败: ${response.status} ${response.statusText}`)
        }

        const blob = await response.blob()
        if (!this.isUsableImageBlob(blob)) {
          throw new Error(`下载内容不是有效图片: size=${blob.size}, type=${blob.type || 'unknown'}`)
        }

        const blobUrl = URL.createObjectURL(blob)

        // 清理缓存（如果超过最大数量）
        if (this.cache.size >= this.maxCacheSize) {
          const firstKey = this.cache.keys().next().value
          const oldUrl = this.cache.get(firstKey)
          if (oldUrl) {
            URL.revokeObjectURL(oldUrl)
            this.cache.delete(firstKey)
          }
        }

        this.cache.set(fileId, blobUrl)
        console.log('[IMAGE_CACHE] 图片缓存成功:', fileId)
        return blobUrl
      } catch (error) {
        lastError = error
        // 刚登录时 token / 上下文可能尚未就绪，短暂退避后重试
        if (attempt < maxAttempts - 1) {
          await new Promise<void>(resolve => setTimeout(resolve, 300 * (attempt + 1)))
        }
      }
    }

    console.error('[IMAGE_CACHE] 图片缓存失败:', lastError)
    // 失败时不回退无鉴权直链（<img> 带不了 Authorization，且绝对域名在自定义域下会 DNS/CORS 失败）
    // 也不写入缓存，保证后续 hydrate / 回到前台刷新还能重新拉取，而不是被坏缓存锁死
    return ''
  }

  /**
   * 校验下载内容是否为可用图片。
   * 后端在下载失败时可能返回 200 空 body 或 XML/HTML/JSON 报错负载，需据此判定为失败。
   */
  private isUsableImageBlob(blob: Blob): boolean {
    if (blob.size === 0) {
      return false
    }
    const type = (blob.type || '').toLowerCase()
    if (type.startsWith('image/')) {
      return true
    }
    // 明确是文本 / 报错负载（xml、html、json 等）直接判失败
    if (type.startsWith('text/') || type.includes('xml') || type.includes('json') || type.includes('html')) {
      return false
    }
    // 未知 / application/octet-stream：存储服务可能不回传准确 MIME，只要非空即放行
    return true
  }

  /**
   * 文件下载路径：始终用当前站点同源相对路径。
   * 禁止拼 VITE_API_BASE_URL 绝对域名——自定义域名（如 o-lvo-p.cargoware.com）
   * 下会打到 controltower-tenant.walltechsystem.com 导致 UNKNOWN_HOST / CORS。
   */
  private getDownloadUrl(fileId: string): string {
    return `/gateway/universal/storage/findByFileId/${fileId}`
  }

  // 清理指定文件的缓存
  clearCache(fileId: string) {
    const url = this.cache.get(fileId)
    if (url) {
      URL.revokeObjectURL(url)
      this.cache.delete(fileId)
      console.log('[IMAGE_CACHE] 清理缓存:', fileId)
    }
  }

  // 清理所有缓存
  clearAllCache() {
    this.cache.forEach(url => {
      URL.revokeObjectURL(url)
    })
    this.cache.clear()
    console.log('[IMAGE_CACHE] 清理所有缓存')
  }
}

// 全局缓存管理器实例
const imageCacheManager = new ImageCacheManager()

/**
 * 带鉴权拉取文件预览地址（Authorization + blob URL）
 * 供侧栏 Logo / favicon 等非组件场景复用，勿用 ?token= 直链（存储接口不认）
 */
export async function resolveFilePreviewUrl(fileId: string): Promise<string> {
  if (!fileId) return ''
  return imageCacheManager.cacheImage(fileId)
}

export function useFileUpload() {
  const uploading = ref(false)

  /**
   * 直接上传文件到云存储
   */
  const uploadToCloudStorage = async(file: File, additionalParams: Record<string, string> = {}) => {
    try {
      uploading.value = true

      const formData = new FormData()
      formData.append('file', file)

      // 添加额外参数
      Object.entries(additionalParams).forEach(([key, value]) => {
        formData.append(key, value)
      })

      console.log('[FILE_UPLOAD] 上传文件到云存储:', { fileName: file.name, params: additionalParams })

      const response = await request.post<string>('/universal/storage/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response) {
        console.log('[FILE_UPLOAD] 文件上传成功，获得文件ID:', response)
        return response // 直接返回文件ID
      } else {
        throw new Error('云存储上传失败：未获得文件ID')
      }
    } catch (error) {
      console.error('[FILE_UPLOAD] 云存储上传失败:', error)
      throw error
    } finally {
      uploading.value = false
    }
  }

  /**
   * 上传Logo文件
   */
  const uploadLogoFile = async(file: File, type: 'header' | 'footer') => {
    try {
      const fileId = await uploadToCloudStorage(file, { type: `logo_${type}` })
      console.log('[FILE_UPLOAD] Logo上传成功:', { type, fileId })

      // 缓存新上传的图片
      const previewUrl = await imageCacheManager.cacheImage(fileId)

      // 返回文件ID（直接保存到配置中）
      return {
        fileId,
        previewUrl // 使用缓存的图片URL
      }
    } catch (error) {
      console.error('[FILE_UPLOAD] Logo上传失败:', error)
      Message.error('Logo上传失败')
      return null
    }
  }

  /**
   * 上传社交媒体二维码
   */
  const uploadSocialQRFile = async(file: File, platform: string) => {
    try {
      const fileId = await uploadToCloudStorage(file, { type: 'social_qr', platform })
      console.log('[FILE_UPLOAD] 社交媒体文件上传成功:', { platform, fileId })

      // 缓存新上传的图片
      const previewUrl = await imageCacheManager.cacheImage(fileId)

      // 返回文件ID（直接保存到配置中）
      return {
        fileId,
        previewUrl // 使用缓存的图片URL
      }
    } catch (error) {
      console.error('[FILE_UPLOAD] 社交媒体文件上传失败:', error)
      Message.error('文件上传失败')
      return null
    }
  }

  /**
   * 删除文件
   */
  const deleteFileById = async(fileId: string) => {
    try {
      const result = await request.post<string>(`/universal/storage/deleteFile/${fileId}`)
      if (result) {
        // 清理本地缓存
        imageCacheManager.clearCache(fileId)
        console.log('[FILE_UPLOAD] 文件删除成功:', fileId)
        return true
      } else {
        console.error('[FILE_UPLOAD] 文件删除失败:', fileId)
        return false
      }
    } catch (error) {
      console.error('[FILE_UPLOAD] 文件删除失败:', fileId, error)
      return false
    }
  }

  /**
   * 批量删除文件（静默删除，不显示提示）
   */
  const deleteFilesByIds = async(fileIds: string[]) => {
    const results = await Promise.allSettled(
      fileIds.map(fileId => deleteFileById(fileId))
    )

    const successCount = results.filter(r => r.status === 'fulfilled' && r.value === true).length
    const failCount = fileIds.length - successCount

    console.log('[FILE_UPLOAD] 批量删除结果:', {
      total: fileIds.length,
      success: successCount,
      failed: failCount,
      fileIds
    })

    return successCount === fileIds.length
  }

  /**
   * 获取文件预览URL（使用缓存）
   */
  const getFileUrl = async(fileId: string): Promise<string> => {
    if (!fileId) return ''

    try {
      // 使用缓存管理器获取图片URL（同源 + Authorization → blob）
      return await imageCacheManager.cacheImage(fileId)
    } catch (error) {
      console.error('[FILE_UPLOAD] 获取文件URL失败:', error)
      return ''
    }
  }

  /**
   * 同步版本：仅返回同源相对路径（兼容旧调用）。
   * 存储接口不认 ?token=，预览请优先用 getFileUrl / resolveFilePreviewUrl。
   */
  const getFileUrlSync = (fileId: string): string => {
    if (!fileId) return ''
    return `/gateway/universal/storage/findByFileId/${fileId}`
  }

  /**
   * 清理文件缓存
   */
  const clearImageCache = (fileId?: string) => {
    if (fileId) {
      imageCacheManager.clearCache(fileId)
    } else {
      imageCacheManager.clearAllCache()
    }
  }

  return {
    uploading,
    uploadLogoFile,
    uploadSocialQRFile,
    uploadToCloudStorage,
    deleteFileById,
    deleteFilesByIds,
    getFileUrl,
    getFileUrlSync,
    clearImageCache
  }
}
